import httpClient from 'hub-http/clients/apiClient';
import { useEffect, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import { createRpcClientV1 } from 'rpc-client-utils';
import { getTranslatedGrowthFeaturesFromProductsRpc } from 'feature-store-service-types';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { getI18nLangLocale } from 'pricing-pages-lib/utils/languageUtils';
import Raven from 'raven-js';
import { FREE, STARTER } from 'self-service-api/constants/ProductLevels';
import { marketingPricing } from 'self-service-api/core/utilities/links';
import { getCanViewNewSeats } from 'ui-addon-upgrades/_core/common/eligibility/getCanViewNewSeats';
import { buildFilteredHighlightedProducts, getProductLineFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import ApiNameToUpgradeProductMap from 'self-service-api/constants/ApiNameToUpgradeProductMap';
import { UPGRADE_PAGE } from 'ui-addon-upgrades/_core/common/constants/PurchaseMotionLocations';
import UIPurchaseMotionButton from 'ui-addon-upgrades/button/UIPurchaseMotionButton';
import { FeatureTableBadge } from 'ui-addon-upgrades/featureTable/sharedComponents';
import ProductIcon from '../../components/common/ProductIcon';
import ProductInfoContainer from '../../components/common/ProductInfoContainer';
import SkeletonLoadWrapper from '../../components/common/loading/SkeletonLoadWrapper';
import { HUB_STARTER_UPGRADE_PAGE } from '../../constants/ErrorBoundaryNames';
import { APP, MERCH_CARD, SCREENS, UPGRADE_PAGES } from '../../constants/tracking/Locations';
import { useProductConfigurationsFromProductLineAndLevel } from '../../hooks/upgradePageHooks';
import ComparePlansLink from '../ComparePlansLink';
import CurrentPlanButton from '../CurrentPlanButton';
import { withErrorBoundary } from '../ErrorBoundary';
import UpgradeHeroBanner from '../UpgradeHeroBanner';
import { buildFreeI18nKeyByProductLine, buildStarterI18nKeyByProductLine } from '../utils/buildI18nKeys';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const buildHubStarterHeaderData = ({
  isLoading,
  productLine,
  productConfigurations,
  upgradePointApiName,
  upgradeConfig,
  upgradeKey,
  purchaseMotions,
  handlePurchaseMotionButtonClick,
  upgradeSource
}) => {
  const upgradeData = {
    apiName: upgradePointApiName,
    apiNames: upgradeConfig.apiNames,
    app: APP,
    screen: SCREENS.UPGRADE_PAGE,
    uniqueId: `${SCREENS.UPGRADE_PAGE}-${ApiNameToUpgradeProductMap[upgradePointApiName]}-feature-table`,
    upgradeKey,
    upgradeProduct: ApiNameToUpgradeProductMap[upgradePointApiName],
    //hacky but our typing is out of date
    upgradeSource
  };
  return {
    FREE: {
      Content: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: buildFreeI18nKeyByProductLine(productLine)
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "m-top-6",
          children: /*#__PURE__*/_jsx(CurrentPlanButton, {
            ctaStyles: {
              padding: '11px',
              visibility: 'visible',
              minWidth: '154px;'
            }
          })
        })]
      })
    },
    STARTER: {
      Content: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FeatureTableBadge, {
          badgeStyle: {
            margin: '0 auto',
            width: 'fit-content',
            marginBottom: '8px'
          },
          badgeType: "recommended"
        }), /*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: buildStarterI18nKeyByProductLine(productLine)
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "m-y-1"
        }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: isLoading,
          heights: [40],
          children: productConfigurations && productConfigurations.length && /*#__PURE__*/_jsx("div", {
            className: "m-top-4",
            children: /*#__PURE__*/_jsx(UIPurchaseMotionButton, {
              purchaseMotions: [purchaseMotions[0]],
              upgradeData: upgradeData,
              level: 1,
              onClick: handlePurchaseMotionButtonClick,
              location: UPGRADE_PAGE,
              trackingSource: MERCH_CARD
            })
          })
        })]
      })
    }
  };
};
const buildHubStarterBodyData = (features, productLine) => {
  return {
    sharedData: {
      levels: [FREE, STARTER],
      subscreen: UPGRADE_PAGES.SUITE_STARTER
    },
    sectionData: {
      data: {
        features: features || [],
        alwaysExpanded: true,
        LabelContent: /*#__PURE__*/_jsxs("div", {
          className: "display-flex align-center",
          children: [/*#__PURE__*/_jsx(ProductIcon, {
            productLine: productLine,
            width: "24",
            className: "m-right-3"
          }), /*#__PURE__*/_jsx("strong", {
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "suiteFeatureTable.section.title",
              options: {
                product: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `productLine.${productLine}`
                })
              }
            })
          })]
        })
      }
    }
  };
};
const HubStarterUpgradePage = ({
  upgradePointApiName,
  upgradeConfig,
  purchaseMotions,
  handlePurchaseMotionButtonClick,
  upgradeSource
}) => {
  const [fetchedData, setFetchedData] = useState({
    features: [],
    canViewNewSeats: false,
    loading: true
  });
  const productLine = getProductLineFromApiNameString(upgradePointApiName);
  useEffect(() => {
    getUsageTracker().setProperties({
      screen: SCREENS.UPGRADE_PAGE,
      subscreen: UPGRADE_PAGES.STARTER
    });
    getUsageTracker().track('upgradesInteraction', {
      action: 'viewed upgrade page',
      upgradeSource,
      upgradeKey: upgradeConfig.upgradeKey,
      apiName: upgradePointApiName,
      pageType: UPGRADE_PAGES.STARTER
    });
    // keep empty dependency array so not to repeat event and exposure log
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const highlightedProducts = buildFilteredHighlightedProducts({
      productLine,
      productLevels: [FREE, STARTER]
    });
    const rpcClient = createRpcClientV1({
      hubHttpClient: httpClient
    });
    const getFeaturesPromise = rpcClient.call(getTranslatedGrowthFeaturesFromProductsRpc, {
      queryParameters: {
        productApiNames: highlightedProducts,
        i18nLangLocale: getI18nLangLocale()
      }
    }).then(response => {
      return response;
    }).catch(error => {
      if (error && error.status !== 0) {
        Raven.captureException(new Error(`Failed to fetch SuiteStarterFeatureTableContainer data for productLine ${productLine}`), {
          extra: {
            message: error.message,
            apiName: upgradePointApiName,
            file: 'HubStarterUpgradePage'
          }
        });
      }
    });
    Promise.all([getFeaturesPromise, getCanViewNewSeats()]).then(([featureResponse, canViewNewSeatsResponse]) => {
      setFetchedData({
        features: featureResponse || [],
        canViewNewSeats: !!canViewNewSeatsResponse,
        loading: false
      });
    }).catch(error => {
      Raven.captureException(new Error(`Failed to fetch SuiteStarterFeatureTableContainer data`), {
        extra: {
          message: error.message,
          apiName: upgradePointApiName
        }
      });
    });

    // keep empty dependency array so it only fetches everything once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const productConfigurations = useProductConfigurationsFromProductLineAndLevel({
    productLine,
    productLevel: STARTER
  });
  const headerData = buildHubStarterHeaderData({
    isLoading: fetchedData.loading,
    productLine,
    productConfigurations,
    upgradeConfig,
    upgradeKey: upgradeConfig.upgradeKey,
    upgradePointApiName,
    purchaseMotions,
    handlePurchaseMotionButtonClick,
    upgradeSource
  });
  const bodyData = buildHubStarterBodyData(fetchedData.features, productLine);
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "starter-upgrade-page",
    className: "p-bottom-12",
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "STARTER_HUB_UPGRADE_PAGE"
    }), /*#__PURE__*/_jsx(UpgradeHeroBanner, {
      upgradeConfig: upgradeConfig,
      upgradePointApiName: upgradePointApiName,
      purchaseMotions: purchaseMotions,
      handlePurchaseMotionButtonClick: handlePurchaseMotionButtonClick
    }), /*#__PURE__*/_jsxs(ProductInfoContainer, {
      style: {
        margin: 'auto'
      },
      children: [/*#__PURE__*/_jsx(ComparePlansLink, {}), /*#__PURE__*/_jsx(FeatureTable, {
        headerData: headerData,
        bodyData: bodyData,
        canViewNewSeats: fetchedData.canViewNewSeats
      }), /*#__PURE__*/_jsx(NavMarker, {
        name: "HUB_FEATURE_TABLE"
      })]
    })]
  });
};
export default withErrorBoundary(HubStarterUpgradePage, {
  boundaryName: HUB_STARTER_UPGRADE_PAGE,
  redirectCallback: marketingPricing
});